import { motion } from 'framer-motion';
import { Icon } from '../ui/Icon';
import { Button } from '../ui/Button';
import { useNavigation } from '../../hooks/useNavigation';

const BentoCard = ({
  children,
  className = "",
  delay = 0
}: {
  children: React.ReactNode,
  className?: string,
  delay?: number
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
    className={`bg-gray-50 rounded-3xl p-8 border border-gray-100 hover:border-orange-200 transition-colors relative overflow-hidden group ${className}`}
  >
    {children}
  </motion.div>
);

export default function TradingSolutions() {
  const { goTo } = useNavigation();

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 md:flex justify-between items-end">
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Complete Trading <span className="text-orange-500">Solutions</span>
            </h2>
            <p className="text-xl text-gray-500 leading-relaxed">
              From sourcing to delivery, we handle every aspect of the supply chain with precision and expertise.
            </p>
          </div>
          <Button
            variant="outline"
            className="hidden md:flex mt-4 md:mt-0 border-gray-200"
            onClick={() => goTo('/about')}
            rightIcon={<Icon name="arrow-right" className="w-4 h-4" />}
          >
            View All Services
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 auto-rows-[minmax(180px,auto)]">
          {/* Large Card: Global Network */}
          <BentoCard className="md:col-span-2 lg:col-span-2 md:row-span-2 bg-gradient-to-br from-gray-900 to-gray-800 text-white !border-none">
            <div className="absolute top-0 right-0 p-8 opacity-20">
              <Icon name="globe" size={120} className="stroke-1" />
            </div>
            <div className="relative h-full flex flex-col justify-between z-10">
              <div>
                <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center mb-6">
                  <Icon name="globe" className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-3xl font-bold mb-4">Global Network</h3>
                <p className="text-gray-400 text-lg leading-relaxed max-w-sm">
                  Extensive connections with trusted suppliers worldwide, ensuring quality from source to destination.
                </p>
              </div>
              <div className="mt-8 flex gap-4">
                <div className="px-4 py-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10">
                  <span className="block text-2xl font-bold text-orange-400">50+</span>
                  <span className="text-xs text-gray-400">Countries</span>
                </div>
                <div className="px-4 py-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10">
                  <span className="block text-2xl font-bold text-orange-400">500+</span>
                  <span className="text-xs text-gray-400">Partners</span>
                </div>
              </div>
            </div>
          </BentoCard>

          {/* Medium Card: One-stop Solution */}
          <BentoCard className="md:col-span-1 lg:col-span-2 bg-orange-50 hover:bg-orange-100/50" delay={0.1}>
            <div className="flex flex-col h-full justify-between">
              <div className="flex justify-between items-start">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600">
                  <Icon name="arrow-right-left" className="w-5 h-5" />
                </div>
                <Icon name="arrow-up-right" className="w-5 h-5 text-gray-300 group-hover:text-orange-500 transition-colors" />
              </div>
              <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-900 mb-2">One-Stop Solution</h3>
                <p className="text-gray-500 text-sm">
                  Seamless end-to-end supply chain management for efficiency.
                </p>
              </div>
            </div>
          </BentoCard>

          {/* Small Card: Support */}
          <BentoCard delay={0.2} className="group hover:bg-gray-900 hover:text-white transition-all duration-300">
            <div className="h-full flex flex-col justify-between">
              <Icon name="heart" className="w-8 h-8 text-orange-500" />
              <div>
                <h3 className="text-lg font-bold mb-1">24/7 Support</h3>
                <p className="text-sm text-gray-500 group-hover:text-gray-400">Dedicated expert team</p>
              </div>
            </div>
          </BentoCard>

          {/* Small Card: Quality */}
          <BentoCard delay={0.3}>
            <div className="h-full flex flex-col justify-between">
              <Icon name="check-circle" className="w-8 h-8 text-green-500" />
              <div>
                <h3 className="text-lg font-bold mb-1">Premium Quality</h3>
                <p className="text-sm text-gray-500">Verified sourcing</p>
              </div>
            </div>
          </BentoCard>

          {/* Wide Card: Logistics */}
          <BentoCard className="md:col-span-2 lg:col-span-2 overflow-hidden" delay={0.2}>
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-2xl -mr-10 -mt-10" />
            <div className="flex items-center gap-6 h-full">
              <div className="shrink-0 w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600">
                <Icon name="truck" className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Efficient Logistics</h3>
                <p className="text-gray-500">
                  Advanced tracking and streamlined shipping processes to ensure timely delivery of your goods.
                </p>
              </div>
            </div>
          </BentoCard>

        </div>
      </div>
    </section>
  );
}
